import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { SegmentedControl } from '@kerfjs/ui/segmented-control';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import { type SafeHtml } from 'kerfjs';
import { ChartNoAxesCombined, Files, Info, ListChecks, ReceiptText } from 'lucide';

import { editorSegment, quoteMatrix, workspace } from '../../../data/state.js';
import { currentCapabilities } from '../../../lib/context.js';

export function DemandSegmentsControl(): SafeHtml {
  const ws = workspace.value;
  if (ws === null) return <></>;
  // Documents counts its files plus the demand's own notes (HS2-E33XQD).
  const demandNoteCount = ((ws.notes ?? []) as { lineItemId: string | null }[]).filter(
    (note) => note.lineItemId === null,
  ).length;
  const lines = ((ws.lineItems ?? []) as unknown[]).length;
  const matrix = quoteMatrix.value;
  const quoteParts =
    matrix !== null
      ? matrix.lineItems.reduce(
          (count, line) =>
            count +
            matrix.columns.filter((column) => (matrix.cells[line.id]?.[column.quotationId]?.basePrice ?? null) !== null)
              .length,
          0,
        ) + matrix.unmatched.length
      : ((ws.quotations ?? []) as unknown[]).length;
  const docCount =
    Object.values((ws.filesByKind ?? {}) as Record<string, unknown[]>).reduce((count, docs) => count + docs.length, 0) +
    demandNoteCount;
  const selectedSegment = editorSegment.value === 'decision-summary' ? 'summary' : editorSegment.value;
  const commercial = currentCapabilities().commercialDemand;
  return (
    <ToolbarControlGroup label="Demand Sections" appearance="contained">
      <SegmentedControl
        id="editor-seg"
        label="Demand sections"
        value={selectedSegment}
        action="editor-seg"
        appearance="toolbar"
        shape="pill"
        size="small"
        choices={[
          { value: 'info', label: 'Info', title: 'Info', content: <LucideIcon icon={Info} name="Info" /> },
          {
            value: 'lines',
            label: 'Line Items',
            title: `Line Items (${String(lines)})`,
            content: <LucideIcon icon={ListChecks} name="Line Items" />,
          },
          ...(commercial
            ? [
                {
                  value: 'quotes',
                  label: 'Quotes',
                  title: `Quotes (${String(quoteParts)})`,
                  content: <LucideIcon icon={ReceiptText} name="Quotes" />,
                },
              ]
            : []),
          {
            value: 'files',
            label: 'Documents',
            title: `Documents (${String(docCount)})`,
            content: <LucideIcon icon={Files} name="Documents" />,
          },
          ...(commercial
            ? [
                {
                  value: 'summary',
                  label: 'Summary / Outcomes',
                  title: 'Summary / Outcomes',
                  content: <LucideIcon icon={ChartNoAxesCombined} name="Summary / Outcomes" />,
                },
              ]
            : []),
        ]}
      />
    </ToolbarControlGroup>
  );
}
